/**
 * AdminCreateGymPage Component
 *
 * Admin page for creating gyms directly (bypassing the approval flow).
 * Allows admins to create gyms and assign owners immediately.
 */

import { useState, useEffect, type FormEvent, type CSSProperties } from 'react';
import { collection, doc, setDoc, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { getGymMembershipId } from '@flash/shared';
import type { User, GymMembership } from '@flash/shared';

/**
 * AdminCreateGymPage allows admins to create gyms directly
 */
export const AdminCreateGymPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('USA');
  const [ownerId, setOwnerId] = useState('');

  // Fetch users for owner selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const db = getFirebaseDb();
        const snapshot = await getDocs(collection(db, 'users'));
        const usersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as User[];
        setUsers(usersData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError('Gym name is required');
      return;
    }
    if (!ownerId) {
      setError('Please select an owner');
      return;
    }

    setSubmitting(true);
    try {
      const db = getFirebaseDb();

      // Create gym document
      // Note: Wall sections are now derived from floor plan regions, so no wallSections field
      const gymData = {
        name: name.trim(),
        description: description.trim() || null,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
        logoURL: null,
        colorGrades: [
          { color: '#22c55e', displayName: 'Green (Beginner)', suggestedVGrade: 'V0' },
          { color: '#eab308', displayName: 'Yellow (Intermediate)', suggestedVGrade: 'V3' },
          { color: '#ef4444', displayName: 'Red (Advanced)', suggestedVGrade: 'V6' },
          { color: '#000000', displayName: 'Black (Expert)', suggestedVGrade: 'V9' },
        ],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const gymRef = await addDoc(collection(db, 'gyms'), gymData);

      // Create owner membership
      const membershipId = getGymMembershipId(ownerId, gymRef.id);
      const membershipData: Omit<GymMembership, 'id' | 'createdAt' | 'updatedAt'> & {
        createdAt: ReturnType<typeof serverTimestamp>;
        updatedAt: ReturnType<typeof serverTimestamp>;
      } = {
        odKey: membershipId,
        userId: ownerId,
        gymId: gymRef.id,
        role: 'owner',
        isHomeGym: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'gymMemberships', membershipId), membershipData);

      setSuccess(`Gym "${name}" created successfully! ID: ${gymRef.id}`);

      // Reset form
      setName('');
      setDescription('');
      setAddress('');
      setCity('');
      setState('');
      setOwnerId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create gym');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Create Gym</h1>
          <p style={subtitleStyle}>
            Create a gym directly without going through the approval process
          </p>
        </header>

        {/* Form */}
        <div style={formContainerStyle}>
          <form onSubmit={handleSubmit}>
            {error && <div style={errorMessageStyle}>{error}</div>}
            {success && <div style={successMessageStyle}>{success}</div>}

            <div style={formGridStyle}>
              {/* Gym Name */}
              <div style={formGroupStyle}>
                <label htmlFor="gymName" style={labelStyle}>
                  Gym Name *
                </label>
                <input
                  id="gymName"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="Boulder World"
                />
              </div>

              {/* Description */}
              <div style={formGroupFullStyle}>
                <label htmlFor="description" style={labelStyle}>
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  style={textareaStyle}
                  placeholder="A great bouldering gym..."
                />
              </div>

              {/* Address */}
              <div style={formGroupFullStyle}>
                <label htmlFor="address" style={labelStyle}>
                  Address
                </label>
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="123 Main St"
                />
              </div>

              {/* City */}
              <div style={formGroupStyle}>
                <label htmlFor="city" style={labelStyle}>
                  City
                </label>
                <input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="Austin"
                />
              </div>

              {/* State */}
              <div style={formGroupStyle}>
                <label htmlFor="state" style={labelStyle}>
                  State
                </label>
                <input
                  id="state"
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="TX"
                />
              </div>

              {/* Country */}
              <div style={formGroupStyle}>
                <label htmlFor="country" style={labelStyle}>
                  Country
                </label>
                <input
                  id="country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={submitting}
                  style={inputStyle}
                  placeholder="USA"
                />
              </div>

              {/* Owner */}
              <div style={formGroupFullStyle}>
                <label htmlFor="owner" style={labelStyle}>
                  Owner *
                </label>
                <select
                  id="owner"
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  disabled={submitting}
                  style={selectStyle}
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName ?? user.email} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                ...submitButtonStyle,
                ...(submitting ? disabledButtonStyle : {}),
              }}
            >
              {submitting ? 'Creating...' : 'Create Gym'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '12px 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const formContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  padding: '24px',
};

const formGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
  marginBottom: '24px',
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const formGroupFullStyle: CSSProperties = {
  ...formGroupStyle,
  gridColumn: 'span 2',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const inputStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  boxSizing: 'border-box',
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  fontFamily: 'inherit',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const submitButtonStyle: CSSProperties = {
  width: '100%',
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.7,
  cursor: 'not-allowed',
};

const errorMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  fontSize: '0.875rem',
  marginBottom: '16px',
};

const successMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#166534',
  fontSize: '0.875rem',
  marginBottom: '16px',
};

export default AdminCreateGymPage;
